/**
 * design_handoff_auth §5 — simplified registration for the Google flow.
 * Google already supplied email and name, so this is three fields: email
 * read-only, name prefilled and editable, workspace name prefilled from
 * the name and editable. No password field — the Google connection is the
 * credential.
 * Created by piotr.pozniak@thebeaverhead.com on 24/11/2022
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { store } from "./../../store";
import { Input, Button, Icon } from "../../theme/untitled";
import AuthAlert from "../AuthPages/AuthAlert";
import { useUserStore } from "./../../../../hooks/redux/user";
import { useAppStore } from "./../../../../hooks/redux/app";
import { formatError } from "../../../../helpers/errors";
import appConfig from "../../../../appConfig";
import styles from "../AuthPages/authForm.module.scss";

/**
 * @param {object} props
 * @param {object} props.oAuthData
 * @param {string} [props.oAuthData.firstName]
 * @param {string} [props.oAuthData.lastName]
 * @param {string} [props.oAuthData.email]
 * @param {string} [props.oAuthData.refreshToken]
 * @param {string} [props.oAuthData.accessToken]
 * @param {number} [props.oAuthData.expiresIn]
 * @param {string} [props.oAuthData.organizationName]
 * @param {string} [props.oAuthData.organization_uuid]
 * @param {string} [props.submitBtnText]
 */
const SignUpForm = (props) => {
  const { user, activateAccount } = useUserStore();

  const fullName = `${props.oAuthData.firstName || ""} ${props.oAuthData.lastName || ""}`.trim();

  const [state, setState] = useState({
    name: fullName,
    organizationName: props.oAuthData.organizationName || `${fullName}'s workspace`,
    organization_uuid: props.oAuthData.organizationUuid,
    coupon: "",
    hasCoupon: false,
  });

  const history = useHistory();
  const { clearErrors } = useAppStore();

  useEffect(() => {
    return clearErrors;
  }, []);

  const onNameChange = useCallback((e) => {
    setState((prev) => ({ ...prev, name: e.target.value }));
  }, []);

  const onWorkspaceChange = useCallback((e) => {
    setState((prev) => ({ ...prev, organizationName: e.target.value }));
  }, []);

  const onCouponChange = useCallback((e) => {
    setState((prev) => ({ ...prev, coupon: e.target.value }));
  }, []);

  const onRegisterClick = async (e) => {
    e.preventDefault();

    const nameParts = state.name.trim().split(/\s+/);
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ");

    await activateAccount({
      first_name: firstName,
      last_name: lastName,
      email: props.oAuthData.email,
      trial_coupon: state.coupon,
      access_token: props.oAuthData.accessToken,
      organization_name: state.organizationName,
      organization_uuid: state.organization_uuid,
    });

    if (store.getState().user.registerSuccess) {
      history.push("/onboarding");
    }
  };

  const onHaveInvitationCode = (e) => {
    e.preventDefault();
    setState((prev) => ({ ...prev, hasCoupon: true }));
  };

  const isLoading = user.register;

  const submitBtnText = props.submitBtnText || "Create free account";

  const apiErrorMessage = useMemo(() => {
    if (!user.registerError) return null;
    return formatError(user.registerError, "Could not create an account.").join(" ");
  }, [user.registerError]);

  return (
    <form action="/" noValidate>
      <h1 className={styles.title}>Confirm your details</h1>
      <p className={styles.sub}>Signing up with Google.</p>

      <div className={styles.fields}>
        <Input
          label="Email"
          type="email"
          defaultValue={props.oAuthData.email}
          readOnly
          disabled
          hint="From your Google account."
        />

        <Input
          autoFocus
          label="Name"
          type="text"
          value={state.name}
          onChange={onNameChange}
          disabled={isLoading}
        />

        <Input
          label="Workspace"
          type="text"
          value={state.organizationName}
          onChange={onWorkspaceChange}
          disabled={isLoading || Boolean(state.organization_uuid)}
        />

        {!state.hasCoupon && (
          <>
            <button type="button" className={styles.linkButton} onClick={onHaveInvitationCode}>
              Use an invite code
            </button>
          </>
        )}
        {state.hasCoupon && (
          <Input
            id="invite_code"
            label="Invite code (optional)"
            type="text"
            value={state.coupon}
            onChange={onCouponChange}
            disabled={isLoading}
          />
        )}

        {apiErrorMessage && <AuthAlert variant="error">{apiErrorMessage}</AuthAlert>}

        <Button
          type="submit"
          fullWidth
          loading={isLoading}
          onClick={onRegisterClick}
          iconTrailing={<Icon name="ArrowRight" size={20} />}
        >
          {submitBtnText}
        </Button>

        <p className={styles.hint}>
          <Icon name="Lock01" size={16} />
          <span>
            By continuing you agree to the{" "}
            <a href={appConfig.tos_link} target="_app-tos">
              Terms
            </a>{" "}
            and{" "}
            <a href={appConfig.pp_link} target="_app-pp">
              Privacy Policy
            </a>
            .{" "}
            
          </span>
        </p>
      </div>

      <p className={styles.footLink}>
        <Link to="/login">Back to log in</Link>
      </p>
    </form>
  );
};

export default SignUpForm;
